import CustomBox from "../../../../../Components/UI/CustomBox/CustomBox/CustomBox";
import DetailsTable from "./components/DetailsTable";
import { ENGINES, SEVERITIES, cleanRuleName, prepareData } from "./components/threatDetailsUtils";

import "./flatStyles.scss";

// A single machine can match hundreds of Sigma rules, and html2canvas
// rasterizes the whole DOM at scale 2 — an unbounded list runs it out of
// memory. The CSV export carries the full set, so nothing is lost.
const MAX_RULES_PER_GROUP = 25;

const severityTitleModifier = (severity) =>
  `thunter-threat-details-flat__severity-title--${severity.toLowerCase()}`;

// The interactive widget is tabs plus a master/detail pane, so html2canvas
// would only ever capture one severity and one selected rule. This variant
// holds no state and prints every rule in every bucket.
const ThreatDetailsFlat = ({ data }) => {
  const bySeverity = SEVERITIES.map((severity) => {
    const bucket = data?.[severity.toLowerCase()];

    const groups = ENGINES.map(({ key, label }) => ({
      label,
      rules: prepareData(bucket, key),
    })).filter((group) => group.rules.length > 0);

    return {
      severity,
      groups,
      total: groups.reduce((sum, group) => sum + group.rules.length, 0),
    };
  }).filter((entry) => entry.total > 0);

  if (bySeverity.length === 0) {
    return (
      <CustomBox className="thunter-threat-details-flat">
        <h2 className="thunter-threat-details-flat__title">Threat Details by Severity</h2>
        <p className="thunter-threat-details-flat__empty">No detections found</p>
      </CustomBox>
    );
  }

  return (
    <CustomBox className="thunter-threat-details-flat">
      <h2 className="thunter-threat-details-flat__title">Threat Details by Severity</h2>

      <div className="thunter-threat-details-flat__content">
        {bySeverity.map(({ severity, groups, total }) => (
          <div className="thunter-threat-details-flat__severity" key={severity}>
            <h3
              className={`thunter-threat-details-flat__severity-title ${severityTitleModifier(
                severity,
              )}`}>
              {severity} Severity Detections ({total})
            </h3>

            {groups.map(({ label, rules }) => (
              <div className="thunter-threat-details-flat__engine" key={label}>
                <h4 className="thunter-threat-details-flat__engine-title">
                  {label} ({rules.length})
                </h4>

                {rules.slice(0, MAX_RULES_PER_GROUP).map((rule, index) => (
                  <div className="thunter-threat-details-flat__rule" key={rule.id || index}>
                    <p className="thunter-threat-details-flat__rule-name">{rule.value}</p>

                    {rule.type === "sigma" ? (
                      <>
                        <DetailsTable
                          hideTabButton={true}
                          data={{
                            Severity: severity,
                            "Rule Name": rule.rule_name,
                            "Rule Description": rule.rule_description || "-",
                            Selector: rule.selector || "-",
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <p className="thunter-threat-details-flat__rule-description">
                          <span>Description:</span> {rule.description || "-"}
                        </p>
                        {/* Yara/CAV rules carry one entry per matched file */}
                        {(rule.matches || []).map((match, matchIndex) => (
                          <DetailsTable
                            key={matchIndex}
                            hideTabButton={true}
                            data={{
                              Severity: severity,
                              Name: cleanRuleName(match.threat),
                              File: match.file || "-",
                              "Signature Status": match.signature_status || "-",
                            }}
                          />
                        ))}
                      </>
                    )}
                  </div>
                ))}

                {rules.length > MAX_RULES_PER_GROUP ? (
                  <p className="thunter-threat-details-flat__more">
                    +{rules.length - MAX_RULES_PER_GROUP} more detections — see the CSV export for
                    the full list
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        ))}
      </div>
    </CustomBox>
  );
};

export default ThreatDetailsFlat;
